import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
// UX-6 — Supabase failures come back as PLAIN OBJECTS
// (PostgrestError), so `error.message` alone loses the Postgres code
// translation. parseSupabaseError handles both shapes and turns the
// common codes into copy a photographer can act on.
import debug from '@/lib/debug';

export interface InquiryCategory {
  id: string;
  org_id: string;
  name: string;
  icon: string;
  color: string;
  sort_order: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export type InquiryCategoryInsert = Omit<InquiryCategory, 'id' | 'created_at' | 'updated_at'>;
export type InquiryCategoryUpdate = Partial<InquiryCategoryInsert>;

// Helper to get user's org_id
const getUserOrgId = async (userId: string): Promise<string | null> => {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('org_id')
    .eq('id', userId)
    .maybeSingle();
  
  if (error || !profile?.org_id) return null;
  return profile.org_id;
};

// Fetch all categories for the current org
export const useInquiryCategories = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['inquiry-categories', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const orgId = await getUserOrgId(user.id);
      if (!orgId) return [];
      
      const { data, error } = await supabase
        .from('inquiry_categories')
        .select('*')
        .eq('org_id', orgId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as InquiryCategory[];
    },
    enabled: !!user,
  });
};

// Create a new category
export const useCreateInquiryCategory = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (category: Omit<InquiryCategoryInsert, 'org_id'>) => {
      if (!user) throw new Error('Not authenticated');
      
      const orgId = await getUserOrgId(user.id);
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      
      const { data, error } = await supabase
        .from('inquiry_categories')
        .insert({
          ...category,
          org_id: orgId,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as InquiryCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry-categories'] });
      toast({
        title: 'Category Created',
        description: 'New inquiry category has been added.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

// Update an existing category
export const useUpdateInquiryCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: InquiryCategoryUpdate }) => {
      const { data, error } = await supabase
        .from('inquiry_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as InquiryCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry-categories'] });
      toast({
        title: 'Category Updated',
        description: 'Category has been saved.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

// Delete a category
export const useDeleteInquiryCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('inquiry_categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry-categories'] });
      toast({
        title: 'Category Deleted',
        description: 'Category has been removed.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

// Bulk upsert categories (for save all operation)
export const useSaveInquiryCategories = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (categories: Array<Omit<InquiryCategoryInsert, 'org_id'> & { id?: string }>) => {
      if (!user) throw new Error('Not authenticated');
      
      const orgId = await getUserOrgId(user.id);
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      
      // First, delete all existing categories for this org
      const { error: deleteError } = await supabase
        .from('inquiry_categories')
        .delete()
        .eq('org_id', orgId);
      
      if (deleteError) throw deleteError;
      
      // Then insert all new categories
      if (categories.length > 0) {
        const categoriesToInsert = categories.map((cat, index) => ({
          name: cat.name,
          icon: cat.icon || 'folder',
          color: cat.color || 'hsl(var(--primary))',
          sort_order: index,
          enabled: cat.enabled ?? true,
          org_id: orgId,
        }));
        
        const { data, error } = await supabase
          .from('inquiry_categories')
          .insert(categoriesToInsert)
          .select();
        
        if (error) throw error;
        return data as InquiryCategory[];
      }
      
      return [];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry-categories'] });
      toast({
        title: 'Categories Saved',
        description: 'Your inquiry categories have been saved successfully.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error Saving Categories',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};
